// Dependency-free collapsible JSON tree renderer (plan Part F).
//
// "Show actual output only" — no authoring, no schema. Renders a parsed JSON
// value so workflow authors can see the shape and learn field paths for
// bindings (e.g. `nodes.trigger.output.<field>`). Used by Run history and the
// Designer Test panel. Styled with the shared CSS-variable palette so it
// tracks the active theme.

import { useState } from 'react';

// parseJsonSafe lives in ../lib/jsonParse so this file only exports a component
// (react-refresh/only-export-components). Re-export removed; import where needed.

interface JsonTreeProps {
  /** Already-parsed value (use parseJsonSafe on a JSON string first). */
  value: unknown;
  /** Optional label shown for the root node (e.g. a field name). */
  name?: string;
}

const monoFont = 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)';

const rootStyle: React.CSSProperties = {
  fontFamily: monoFont,
  fontSize: 12.5,
  lineHeight: 1.6,
  color: 'var(--fg)',
  background: 'var(--bg-elev)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '8px 10px',
  // JSON and log output genuinely cannot wrap, so this is the one place a
  // horizontal scroller is the RIGHT answer rather than a failure to reflow.
  // maxWidth pins it to the blade so the scroll stays inside this box instead of
  // widening its flex parent (a flex item's default min-width: auto would let
  // the content push the column out).
  overflowX: 'auto',
  maxWidth: '100%',
  minWidth: 0,
};

export function JsonTree({ value, name }: JsonTreeProps) {
  return (
    <div style={rootStyle}>
      <JsonNode value={value} name={name} depth={0} />
    </div>
  );
}

interface JsonNodeProps {
  value: unknown;
  name?: string;
  depth: number;
}

// Objects/arrays start expanded for the first couple of levels; deeper nodes
// start collapsed to keep large payloads readable.
function JsonNode({ value, name, depth }: JsonNodeProps) {
  const isObject = value !== null && typeof value === 'object';
  const [open, setOpen] = useState(depth < 2);

  const label = name != null ? <span style={{ color: 'var(--fg-muted)' }}>{name}: </span> : null;

  if (!isObject) {
    return (
      <div style={{ paddingLeft: depth === 0 ? 0 : 14 }}>
        {label}
        <Primitive value={value} />
      </div>
    );
  }

  const isArray = Array.isArray(value);
  const entries: [string, unknown][] = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v])
    : Object.entries(value as Record<string, unknown>);

  const open1 = isArray ? '[' : '{';
  const close1 = isArray ? ']' : '}';
  const count = entries.length;

  if (count === 0) {
    return (
      <div style={{ paddingLeft: depth === 0 ? 0 : 14 }}>
        {label}
        <span style={{ color: 'var(--fg-dim)' }}>
          {open1}
          {close1}
        </span>
      </div>
    );
  }

  return (
    <div style={{ paddingLeft: depth === 0 ? 0 : 14 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
        }}
        aria-expanded={open}
      >
        <span style={{ color: 'var(--accent)', marginRight: 4 }}>{open ? '▾' : '▸'}</span>
        {label}
        <span style={{ color: 'var(--fg-dim)' }}>{open1}</span>
        {!open && (
          <span style={{ color: 'var(--fg-dim)' }}>
            {' '}
            {count} {count === 1 ? 'item' : 'items'} {close1}
          </span>
        )}
      </button>
      {open && (
        <div>
          {entries.map(([k, v]) => (
            <JsonNode key={k} name={isArray ? undefined : k} value={v} depth={depth + 1} />
          ))}
          <div style={{ color: 'var(--fg-dim)' }}>{close1}</div>
        </div>
      )}
    </div>
  );
}

function Primitive({ value }: { value: unknown }) {
  if (value === null) {
    return <span style={{ color: 'var(--fg-dim)', fontStyle: 'italic' }}>null</span>;
  }
  switch (typeof value) {
    case 'string':
      return <span style={{ color: 'var(--fg)' }}>&quot;{value}&quot;</span>;
    case 'number':
    case 'bigint':
      return <span style={{ color: 'var(--accent)' }}>{String(value)}</span>;
    case 'boolean':
      return <span style={{ color: 'var(--accent)' }}>{String(value)}</span>;
    default:
      return <span style={{ color: 'var(--fg)' }}>{String(value)}</span>;
  }
}

export default JsonTree;
